import React, { createContext, useContext, useState, useCallback, useRef, useMemo, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { QUERY_CONFIG } from "@/lib/queryConfig";

interface ProfileDisplayName {
  id: string;
  first_name: string;
  last_name: string;
  full_name: string;
  /** True when the user holds the 'client' role (from the v2 RPC). */
  is_client?: boolean;
  /** The user's primary role name (from the v3 RPC). */
  role?: string | null;
}

type ProfileNamesMap = Record<string, ProfileDisplayName>;

interface ProfileNamesContextType {
  profileNames: ProfileNamesMap;
  loading: boolean;
  getDisplayName: (userId: string | null | undefined) => string;
  registerUserIds: (ids: (string | null | undefined)[]) => void;
}

// eslint-disable-next-line react-refresh/only-export-components -- Context co-located with provider
export const ProfileNamesContext = createContext<ProfileNamesContextType | undefined>(undefined);

/**
 * Fetches profile display names for given user IDs.
 * This is the single API call that replaces multiple duplicate calls.
 */
const fetchProfileNames = async (userIds: string[]): Promise<ProfileNamesMap> => {
  if (userIds.length === 0) {
    return {};
  }

  const { data, error } = await supabase.rpc('get_profiles_display_names', {
    _user_ids: userIds
  });

  if (error) {
    /**
     * THROW — never return {} as a "successful" result. profiles use a
     * STATIC cache, so a swallowed transient failure would freeze every
     * name as "Unknown" for the whole session instead of retrying.
     */
    console.error('Error fetching profile display names:', error);
    throw error;
  }

  const namesMap: ProfileNamesMap = {};
  (data || []).forEach((profile: ProfileDisplayName) => {
    namesMap[profile.id] = profile;
  });
  return namesMap;
};

interface ProfileNamesProviderProps {
  children: React.ReactNode;
}

/**
 * ProfileNamesProvider - Single source of truth for profile display names.
 * 
 * This provider solves the N+1 query problem by:
 * 1. Collecting all user IDs from child components
 * 2. Batching them into a SINGLE API call
 * 3. Caching results and sharing via context
 * 
 * Components call registerUserIds() to request names, and the provider
 * batches all requests into one efficient query.
 */
export const ProfileNamesProvider: React.FC<ProfileNamesProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  
  /** Accumulated user IDs from all child components - use Set for deduplication */
  const allIdsRef = useRef<Set<string>>(new Set());
  const [queryIds, setQueryIds] = useState<string[]>([]);
  const batchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [batchVersion, setBatchVersion] = useState(0);

  /** Single query for ALL profile names - uses stable key with version */
  const { data: fetchedNames = {}, isLoading } = useQuery({
    queryKey: ['profile-display-names-global', batchVersion],
    queryFn: () => fetchProfileNames(queryIds),
    enabled: queryIds.length > 0,
    ...QUERY_CONFIG.profiles,
  });

  /** Accumulate all fetched names across batches */
  const [profileNames, setProfileNames] = useState<ProfileNamesMap>({});
  
  useEffect(() => {
    if (Object.keys(fetchedNames).length > 0) {
      setProfileNames(prev => ({ ...prev, ...fetchedNames }));
    }
  }, [fetchedNames]);

  /**
   * Register user IDs that need display names.
   * Uses batching to collect IDs and make a single query.
   */
  const registerUserIds = useCallback((ids: (string | null | undefined)[]) => {
    const validIds = ids.filter((id): id is string => !!id);
    
    /** Check if we need to add any new IDs */
    let hasNewIds = false;
    validIds.forEach(id => {
      if (!allIdsRef.current.has(id) && !profileNames[id]) {
        allIdsRef.current.add(id);
        hasNewIds = true;
      }
    });

    if (!hasNewIds) return;

    /** Debounce: collect all IDs from this render cycle */
    if (batchTimeoutRef.current) {
      clearTimeout(batchTimeoutRef.current);
    }

    batchTimeoutRef.current = setTimeout(() => {
      /** Get only IDs we don't have yet */
      const idsToFetch = [...allIdsRef.current].filter(id => !profileNames[id]);
      if (idsToFetch.length > 0) {
        setQueryIds(idsToFetch.sort());
        setBatchVersion(v => v + 1);
      }
    }, 50); /** Slightly longer debounce to collect more IDs */
  }, [profileNames]);

  const loading = isLoading && queryIds.length > 0;

  const getDisplayName = useCallback((userId: string | null | undefined): string => {
    if (!userId) return "Unknown";
    const profile = profileNames[userId];
    return profile ? profile.full_name : "Unknown";
  }, [profileNames]);

  const value: ProfileNamesContextType = useMemo(() => ({
    profileNames,
    loading,
    getDisplayName,
    registerUserIds,
  }), [profileNames, loading, getDisplayName, registerUserIds]);

  return (
    <ProfileNamesContext.Provider value={value}>
      {children}
    </ProfileNamesContext.Provider>
  );
};

/**
 * useProfileNames - Access profile names from the single source of truth.
 * 
 * Call registerUserIds() with the IDs you need, then use getDisplayName()
 * to retrieve the name. All components share the same cached data.
 */
// eslint-disable-next-line react-refresh/only-export-components -- Hook co-located with context provider
export const useProfileNames = (): ProfileNamesContextType => {
  const context = useContext(ProfileNamesContext);
  if (context === undefined) {
    throw new Error('useProfileNames must be used within a ProfileNamesProvider');
  }
  return context;
};

/**
 * useProfileDisplayNamesFromContext - Drop-in replacement for useProfileDisplayNames.
 * 
 * Components can switch to this hook with minimal changes:
 * - Before: const { getDisplayName } = useProfileDisplayNames(userIds);
 * - After:  const { getDisplayName } = useProfileDisplayNamesFromContext(userIds);
 */
// eslint-disable-next-line react-refresh/only-export-components -- Hook co-located with context provider
export const useProfileDisplayNamesFromContext = (userIds: (string | null | undefined)[]) => {
  const { profileNames, loading, getDisplayName, registerUserIds } = useProfileNames();
  
  /** Register the IDs this component needs */
  React.useEffect(() => {
    registerUserIds(userIds);
  }, [userIds, registerUserIds]);

  return { profileNames, loading, getDisplayName };
};
